import React, { useState, useEffect } from 'react';
import { campaignsAPI, dashboardAPI, reportsAPI, notificationsAPI } from '../utils/api';

const PERIODS = [
  { label: 'Weekly',  days: 7,   key: 'weekly',  icon: '7D' },
  { label: 'Monthly', days: 30,  key: 'monthly', icon: '30D' },
  { label: 'Yearly',  days: 365, key: 'yearly',  icon: '1Y' },
];

const FRAUD_TYPE_DESC = {
  'VPN': 'Competitors and bots hide behind VPN or proxy IPs to click your ads repeatedly while avoiding detection.',
  'Over Threshold': 'Users who clicked your ads more times than your configured threshold allows within a set time window.',
  'Fraudulent Device': 'Clicks from devices exhibiting bot-like behaviour patterns or flagged as suspicious by our detection engine.',
  'Disabled JS': 'Visitors with JavaScript disabled — a common trait of automated bots and scrapers, not genuine users.',
  'Bounced': 'Visitors who left your landing page immediately without any meaningful interaction.',
  'Out of Geo': 'Clicks originating from countries outside your target audience or that are on your block list.',
  'Manually Blocked': 'IP addresses you have manually added to the block list.',
  'Range Blocking': 'IP addresses blocked due to suspicious activity from their IP subnet range.',
  'Not Specified': 'Clicks flagged by our system where the primary signal did not match a named category.',
};

function fmt(n, decimals = 0) {
  if (n == null || n === '') return '—';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function pct(num, den) {
  if (!den) return '0.0';
  return ((num / den) * 100).toFixed(1);
}

function periodLabel(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  const f = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${f(start)} – ${f(end)}`;
}

function isoRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

// ── Auto-generate narrative insights from stats ──────────────────────────────

function generateInsights(stats, campaign) {
  const total = stats.total_clicks || 0;
  const fraud = stats.fraud_clicks || 0;
  const fraudRate = total > 0 ? ((fraud / total) * 100).toFixed(1) : 0;
  const topFraud = Object.entries(stats.fraud_types || {}).sort((a, b) => b[1] - a[1]);
  const devices = stats.device_breakdown || {};
  const totalDev = Object.values(devices).reduce((s, d) => s + (d.total || 0), 0);
  const mobileTot = (devices.mobile?.total || 0) + (devices.tablet?.total || 0);
  const mobilePct = totalDev > 0 ? ((mobileTot / totalDev) * 100).toFixed(0) : 0;
  const mobileFraud = (devices.mobile?.fraud || 0) + (devices.tablet?.fraud || 0);
  const mobileFraudPct = fraud > 0 ? ((mobileFraud / fraud) * 100).toFixed(0) : 0;
  const topCountry = (stats.top_countries || [])[0];
  const topFraudType = topFraud[0];
  const topFraudPct = topFraudType && fraud > 0 ? ((topFraudType[1] / fraud) * 100).toFixed(0) : 0;
  const daily = (stats.daily_breakdown || []);
  const spikeDays = daily.filter(d => {
    const avgFraud = fraud / (daily.length || 1);
    return d.fraud > avgFraud * 2 && d.fraud > 0;
  }).map(d => new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));

  const lines = [];
  if (fraudRate > 0) lines.push(`ClickSaver detected <strong>${fraudRate}%</strong> fraudulent traffic for <strong>${campaign.domain}</strong> during this period — protecting your ad spend from ${fmt(fraud)} invalid clicks.`);
  if (topFraudType) lines.push(`<strong>${topFraudPct}%</strong> of detected fraud originated from <strong>${topFraudType[0]}</strong>. ${FRAUD_TYPE_DESC[topFraudType[0]] || ''}`);
  if (mobileFraudPct > 50) lines.push(`Mobile devices accounted for <strong>${mobileFraudPct}%</strong> of detected fraud. Mobile IPs are easier to rotate, making them a common tool for fraudsters.`);
  if (topCountry && topCountry.fraud > 0) {
    const countryPct = fraud > 0 ? ((topCountry.fraud / fraud) * 100).toFixed(0) : 0;
    lines.push(`Fraud on this domain primarily came from <strong>${topCountry.country}</strong> — <strong>${countryPct}%</strong> of all fraud clicks.`);
  }
  if (spikeDays.length > 0) lines.push(`There was an unexpected spike in fraud on <strong>${spikeDays.slice(0, 2).join(' and ')}</strong> — ClickSaver detected and blocked the IPs involved automatically.`);
  if (stats.estimated_savings > 0) lines.push(`ClickSaver saved you an estimated <strong>$${fmt(stats.estimated_savings, 2)}</strong> in wasted ad spend this period.`);
  return lines;
}

// ── In-app preview card ──────────────────────────────────────────────────────

function MetricTile({ label, value, sub, color, bg, border }) {
  return (
    <div style={{ padding: '16px 18px', borderRadius: 10, background: bg || 'var(--bg-secondary)', border: `1px solid ${border || 'var(--border)'}`, display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0.8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 900, color: color || 'var(--text-primary)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  );
}

function HBar({ label, fraudPct, fraudN, totalN, color }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 11, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
          <span style={{ color, fontWeight: 700 }}>{fraudN}</span> / {totalN}
        </span>
      </div>
      <div style={{ display: 'flex', height: 8, borderRadius: 99, overflow: 'hidden', background: 'rgba(63,185,80,0.15)' }}>
        <div style={{ width: `${100 - parseFloat(fraudPct)}%`, background: 'var(--accent-green)', opacity: 0.6 }} />
        <div style={{ width: `${parseFloat(fraudPct)}%`, background: color }} />
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 4 }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}><span style={{ color: 'var(--accent-green)' }}>■</span> Genuine {(totalN - fraudN).toLocaleString()}</span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}><span style={{ color }}>■</span> Fraud {fraudN.toLocaleString()}</span>
      </div>
    </div>
  );
}

function CampaignReportCard({ campaign, stats, period }) {
  const total = stats.total_clicks || 0;
  const fraud = stats.fraud_clicks || 0;
  const fraudRate = total > 0 ? ((fraud / total) * 100).toFixed(1) : '0.0';
  const rateNum = parseFloat(fraudRate);
  const rateColor = rateNum >= 20 ? 'var(--accent-red)' : rateNum >= 10 ? 'var(--accent-orange)' : 'var(--accent-green)';

  const topFraud = Object.entries(stats.fraud_types || {}).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const topCountries = (stats.top_countries || []).slice(0, 5);
  const maxFraud = topFraud[0]?.[1] || 1;
  const maxCountry = topCountries[0]?.total || 1;

  const devices = stats.device_breakdown || {};
  const deviceRows = Object.entries(devices)
    .map(([name, d]) => ({ name, total: d.total || 0, fraud: d.fraud || 0 }))
    .filter(d => d.total > 0)
    .sort((a, b) => b.total - a.total);
  const maxDev = deviceRows[0]?.total || 1;

  const daily = (stats.daily_breakdown || []).slice(-14);
  const maxDailyTotal = Math.max(...daily.map(d => d.total), 1);

  const insights = generateInsights(stats, campaign);

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', marginBottom: 12 }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', background: 'linear-gradient(135deg, rgba(88,166,255,0.08), rgba(63,185,80,0.06))', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg, #58a6ff, #3fb950)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
            {campaign.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{campaign.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', marginTop: 1 }}>{campaign.domain}</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{period.label} Report</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{periodLabel(period.days)}</div>
        </div>
      </div>

      {/* Fraud rate bar */}
      <div style={{ padding: '10px 24px', borderBottom: '1px solid var(--border)', background: rateNum >= 10 ? `rgba(${rateNum >= 20 ? '248,81,73' : '210,153,34'},0.05)` : 'rgba(63,185,80,0.04)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Fraud Rate</div>
        <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.min(rateNum, 100)}%`, background: rateColor, borderRadius: 99 }} />
        </div>
        <div style={{ fontSize: 15, fontWeight: 800, color: rateColor, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', minWidth: 52, textAlign: 'right' }}>{fraudRate}%</div>
      </div>

      {/* Key metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, padding: '16px 24px' }}>
        <MetricTile label="Total Clicks" value={fmt(total)} color="var(--accent-blue)" />
        <MetricTile label="Fraud Clicks" value={fmt(fraud)} color="var(--accent-red)" bg="rgba(248,81,73,0.05)" border="rgba(248,81,73,0.15)" />
        <MetricTile label="Legitimate" value={fmt(stats.legitimate_clicks)} color="var(--accent-green)" bg="rgba(63,185,80,0.05)" border="rgba(63,185,80,0.15)" />
        <MetricTile label="Blocked IPs" value={fmt(stats.blocked_ips)} color="var(--accent-red)" />
        <MetricTile label="Unique Visitors" value={fmt(stats.unique_visitors)} color="var(--text-primary)" />
        <MetricTile label="Clean Traffic" value={`${stats.cleaner_traffic_percent || 0}%`} color={stats.cleaner_traffic_percent >= 80 ? 'var(--accent-green)' : 'var(--accent-orange)'} />
        <MetricTile label="Clicks Saved" value={fmt(stats.clicks_saved)} color="var(--accent-green)" bg="rgba(63,185,80,0.05)" border="rgba(63,185,80,0.15)" />
        <MetricTile label="Est. Savings" value={`$${fmt(stats.estimated_savings, 2)}`} color="var(--accent-green)" bg="rgba(63,185,80,0.05)" border="rgba(63,185,80,0.15)" />
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div style={{ margin: '0 24px 16px', padding: '14px 18px', background: 'rgba(88,166,255,0.05)', border: '1px solid rgba(88,166,255,0.15)', borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent-blue)', textTransform: 'none', letterSpacing: 0.8, marginBottom: 10 }}>Insights</div>
          {insights.map((line, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: i < insights.length - 1 ? 6 : 0 }}
              dangerouslySetInnerHTML={{ __html: `• ${line}` }} />
          ))}
        </div>
      )}

      {/* Daily activity */}
      {daily.length > 0 && (
        <div style={{ padding: '0 24px 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0.8, marginBottom: 10 }}>Daily Activity</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 56, overflowX: 'auto' }}>
            {daily.map((d, i) => {
              const totalH = Math.max((d.total / maxDailyTotal) * 52, 2);
              const fraudH = d.total > 0 ? (d.fraud / d.total) * totalH : 0;
              const legitH = totalH - fraudH;
              return (
                <div key={i} title={`${d.date}: ${d.total} total, ${d.fraud} fraud`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 14, flex: 1 }}>
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                    <div style={{ height: legitH, background: 'var(--accent-blue)', opacity: 0.5, borderRadius: '2px 2px 0 0' }} />
                    {fraudH > 0 && <div style={{ height: fraudH, background: 'var(--accent-red)', borderRadius: fraudH === totalH ? '2px 2px 0 0' : 0 }} />}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}><span style={{ color: 'var(--accent-blue)' }}>■</span> Legitimate</span>
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}><span style={{ color: 'var(--accent-red)' }}>■</span> Fraud</span>
          </div>
        </div>
      )}

      {/* Device + Countries + Fraud types */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0, borderTop: '1px solid var(--border)' }}>
        {/* Device breakdown */}
        <div style={{ padding: '16px 20px', borderRight: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0.8, marginBottom: 12 }}>Device Breakdown</div>
          {deviceRows.length === 0
            ? <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No data</div>
            : deviceRows.map((d, i) => {
              const fraudPctDev = pct(d.fraud, d.total);
              return (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'capitalize' }}>{d.name}</span>
                    <span style={{ fontSize: 11, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
                      <span style={{ color: 'var(--accent-red)', fontWeight: 700 }}>{d.fraud}</span>/{d.total}
                    </span>
                  </div>
                  <div style={{ height: 6, borderRadius: 99, overflow: 'hidden', background: 'rgba(63,185,80,0.15)', display: 'flex' }}>
                    <div style={{ width: `${100 - parseFloat(fraudPctDev)}%`, background: 'rgba(88,166,255,0.5)' }} />
                    <div style={{ width: `${fraudPctDev}%`, background: 'var(--accent-red)' }} />
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>{fraudPctDev}% fraud rate</div>
                </div>
              );
            })}
        </div>

        {/* Fraud types */}
        <div style={{ padding: '16px 20px', borderRight: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0.8, marginBottom: 12 }}>Top Fraud Types</div>
          {topFraud.length === 0
            ? <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No fraud detected</div>
            : topFraud.map(([type, count], i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{type}</span>
                  <span style={{ fontSize: 11, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--accent-red)', fontWeight: 700 }}>
                    {count} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({pct(count, fraud)}%)</span>
                  </span>
                </div>
                <div style={{ height: 4, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct(count, maxFraud)}%`, background: 'var(--accent-red)', borderRadius: 99 }} />
                </div>
              </div>
            ))}
        </div>

        {/* Countries */}
        <div style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0.8, marginBottom: 12 }}>Top Countries</div>
          {topCountries.length === 0
            ? <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No data</div>
            : topCountries.map((c, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{c.country} <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>{c.code}</span></span>
                  <span style={{ fontSize: 11, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
                    <span style={{ color: 'var(--accent-orange)', fontWeight: 700 }}>{c.fraud}</span>/{c.total}
                  </span>
                </div>
                <div style={{ height: 4, background: 'var(--border)', borderRadius: 99, overflow: 'hidden', display: 'flex' }}>
                  <div style={{ width: `${pct(c.total - c.fraud, maxCountry)}%`, background: 'rgba(88,166,255,0.4)', borderRadius: 99 }} />
                  <div style={{ width: `${pct(c.fraud, maxCountry)}%`, background: 'var(--accent-orange)' }} />
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// ── PDF HTML builder ─────────────────────────────────────────────────────────

function buildPDF(reportData, period) {
  const now = new Date();
  const totalClicks = reportData.reduce((s, r) => s + (r.stats.total_clicks || 0), 0);
  const totalFraud = reportData.reduce((s, r) => s + (r.stats.fraud_clicks || 0), 0);
  const totalSavings = reportData.reduce((s, r) => s + (r.stats.estimated_savings || 0), 0);
  const totalBlocked = reportData.reduce((s, r) => s + (r.stats.blocked_ips || 0), 0);
  const overallRate = totalClicks > 0 ? ((totalFraud / totalClicks) * 100).toFixed(1) : '0.0';

  const campBlocks = reportData.map(({ campaign, stats }) => {
    const total = stats.total_clicks || 0;
    const fraud = stats.fraud_clicks || 0;
    const fraudRate = total > 0 ? ((fraud / total) * 100).toFixed(1) : '0.0';
    const rateNum = parseFloat(fraudRate);
    const rateColor = rateNum >= 20 ? '#dc2626' : rateNum >= 10 ? '#d97706' : '#16a34a';
    const topFraud = Object.entries(stats.fraud_types || {}).sort((a, b) => b[1] - a[1]).slice(0, 5);
    const topCountries = (stats.top_countries || []).slice(0, 5);
    const maxFraud = topFraud[0]?.[1] || 1;
    const maxCountry = topCountries[0]?.total || 1;
    const devices = stats.device_breakdown || {};
    const deviceRows = Object.entries(devices).map(([n, d]) => ({ name: n, total: d.total || 0, fraud: d.fraud || 0 })).filter(d => d.total > 0).sort((a, b) => b.total - a.total);
    const daily = (stats.daily_breakdown || []).slice(-14);
    const maxDaily = Math.max(...daily.map(d => d.total), 1);
    const insights = generateInsights(stats, campaign);

    const insightHtml = insights.length > 0 ? `
      <div class="insights">
        <div class="section-title" style="color:#2563eb;border-color:#bfdbfe">Insights</div>
        ${insights.map(l => `<div class="insight-line">• ${l}</div>`).join('')}
      </div>` : '';

    const dailyHtml = daily.length > 0 ? `
      <div class="daily-section">
        <div class="section-title">Daily Activity — Last ${daily.length} Days</div>
        <div class="daily-chart">
          ${daily.map(d => {
            const h = Math.max((d.total / maxDaily) * 48, 1);
            const fh = d.total > 0 ? (d.fraud / d.total) * h : 0;
            const lh = h - fh;
            const dayLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return `<div class="day-col" title="${dayLabel}: ${d.total} total, ${d.fraud} fraud">
              <div class="day-bar">
                <div style="height:${lh}px;background:#93c5fd;opacity:0.8"></div>
                ${fh > 0 ? `<div style="height:${fh}px;background:#dc2626"></div>` : ''}
              </div>
              <div class="day-label">${dayLabel.replace(' ', '<br/>')}</div>
            </div>`;
          }).join('')}
        </div>
        <div class="legend"><span style="color:#3b82f6">■ Legitimate</span>&nbsp;&nbsp;<span style="color:#dc2626">■ Fraud</span></div>
      </div>` : '';

    const deviceHtml = deviceRows.length > 0 ? `
      <div class="breakdown-col">
        <div class="breakdown-title">Device Breakdown</div>
        ${deviceRows.map(d => {
          const fp = pct(d.fraud, d.total);
          return `<div class="bar-row">
            <div class="bar-label" style="text-transform:capitalize">${d.name}</div>
            <div style="display:flex;height:6px;border-radius:99px;overflow:hidden;margin:3px 0">
              <div style="width:${100 - parseFloat(fp)}%;background:#93c5fd"></div>
              <div style="width:${fp}%;background:#dc2626"></div>
            </div>
            <div style="display:flex;justify-content:space-between;font-size:9px;color:#94a3b8">
              <span>Genuine ${(d.total - d.fraud).toLocaleString()}</span>
              <span style="color:#dc2626">Fraud ${d.fraud.toLocaleString()} (${fp}%)</span>
            </div>
          </div>`;
        }).join('')}
      </div>` : '<div class="breakdown-col"></div>';

    const fraudTypeHtml = `
      <div class="breakdown-col">
        <div class="breakdown-title">Top Fraud Types</div>
        ${topFraud.length === 0 ? '<p class="no-data">No fraud detected this period</p>' : topFraud.map(([t, c]) => `
          <div class="bar-row">
            <div style="display:flex;justify-content:space-between;margin-bottom:3px">
              <span class="bar-label">${t}</span>
              <span style="font-size:10px;font-weight:700;color:#dc2626">${c} <span style="color:#94a3b8;font-weight:400">(${pct(c, fraud)}%)</span></span>
            </div>
            <div class="bar-track"><div class="bar-fill" style="width:${pct(c, maxFraud)}%;background:#dc2626"></div></div>
            ${FRAUD_TYPE_DESC[t] ? `<div style="font-size:9px;color:#94a3b8;margin-top:3px;line-height:1.4">${FRAUD_TYPE_DESC[t].substring(0, 80)}…</div>` : ''}
          </div>`).join('')}
      </div>`;

    const countriesHtml = `
      <div class="breakdown-col">
        <div class="breakdown-title">Top Countries by Fraud</div>
        ${topCountries.length === 0 ? '<p class="no-data">No data</p>' : topCountries.map(c => `
          <div class="bar-row">
            <div style="display:flex;justify-content:space-between;margin-bottom:3px">
              <span class="bar-label">${c.country} <span style="color:#94a3b8;font-size:9px">${c.code}</span></span>
              <span style="font-size:10px;font-weight:700;color:#d97706">${c.fraud}<span style="color:#94a3b8;font-weight:400"> / ${c.total}</span></span>
            </div>
            <div style="display:flex;height:4px;border-radius:99px;overflow:hidden">
              <div style="width:${pct(c.total - c.fraud, maxCountry)}%;background:#93c5fd"></div>
              <div style="width:${pct(c.fraud, maxCountry)}%;background:#d97706"></div>
            </div>
          </div>`).join('')}
      </div>`;

    return `
    <div class="campaign">
      <div class="camp-head">
        <div style="display:flex;align-items:center;gap:14px">
          <div class="avatar">${campaign.name.charAt(0).toUpperCase()}</div>
          <div>
            <div class="camp-name">${campaign.name}</div>
            <div class="camp-domain">${campaign.domain}</div>
          </div>
        </div>
        <div style="text-align:right">
          <div style="font-size:11px;font-weight:700;color:#475569">${period.label} Report</div>
          <div style="font-size:11px;color:#94a3b8;margin-top:2px">${periodLabel(period.days)}</div>
        </div>
      </div>

      <div class="fraud-bar-row">
        <span class="fraud-bar-label">FRAUD RATE</span>
        <div class="fraud-bar-track"><div class="fraud-bar-fill" style="width:${Math.min(rateNum, 100)}%;background:${rateColor}"></div></div>
        <span style="font-size:14px;font-weight:900;font-family:monospace;color:${rateColor};min-width:48px;text-align:right">${fraudRate}%</span>
      </div>

      <div class="metrics">
        ${[
          ['Total Clicks', fmt(total), '#2563eb'],
          ['Fraud Clicks', fmt(fraud), '#dc2626'],
          ['Legitimate', fmt(stats.legitimate_clicks), '#16a34a'],
          ['Blocked IPs', fmt(stats.blocked_ips), '#dc2626'],
          ['Unique Visitors', fmt(stats.unique_visitors), '#0f172a'],
          ['Clean Traffic', `${stats.cleaner_traffic_percent || 0}%`, rateNum <= 10 ? '#16a34a' : '#d97706'],
          ['Clicks Saved', fmt(stats.clicks_saved), '#16a34a'],
          ['Est. Savings', `$${fmt(stats.estimated_savings, 2)}`, '#16a34a'],
        ].map(([label, value, color]) => `<div class="metric"><div class="metric-label">${label}</div><div class="metric-value" style="color:${color}">${value}</div></div>`).join('')}
      </div>

      ${insightHtml}
      ${dailyHtml}

      <div class="breakdown">
        ${deviceHtml}
        ${fraudTypeHtml}
        ${countriesHtml}
      </div>
    </div>`;
  }).join('');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>ClickSaver ${period.label} Report — ${now.toLocaleDateString()}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Inter',system-ui,sans-serif;background:#f8fafc;color:#0f172a;font-size:13px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .page{max-width:960px;margin:0 auto}

  .cover{background:linear-gradient(135deg,#0f172a 0%,#1e293b 60%,#0f172a 100%);padding:44px 52px 40px;color:#fff;display:flex;justify-content:space-between;align-items:flex-start}
  .logo-mark{font-size:24px;font-weight:900;letter-spacing:-0.5px}
  .logo-mark span{color:#58a6ff}
  .logo-sub{font-size:10px;color:#64748b;margin-top:4px;letter-spacing:1px;text-transform:uppercase}
  .report-title{font-size:28px;font-weight:900;color:#fff;letter-spacing:-0.5px}
  .report-sub{font-size:13px;color:#94a3b8;margin-top:8px;line-height:1.7}

  .summary{background:#fff;border-bottom:2px solid #e2e8f0;display:grid;grid-template-columns:repeat(4,1fr)}
  .sum-item{padding:18px 20px;border-right:1px solid #f1f5f9;display:flex;align-items:center;gap:12px}
  .sum-item:last-child{border-right:none}
  .sum-icon{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
  .sum-label{font-size:9px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:3px}
  .sum-val{font-size:22px;font-weight:900;font-family:monospace;line-height:1}

  .body{padding:28px 52px 52px;background:#f8fafc}

  .campaign{background:#fff;border:1px solid #e2e8f0;border-radius:14px;overflow:hidden;margin-bottom:28px;page-break-inside:avoid;box-shadow:0 1px 3px rgba(0,0,0,0.06)}
  .camp-head{padding:16px 22px;background:linear-gradient(135deg,#f8fafc,#f1f5f9);border-bottom:1px solid #e2e8f0;display:flex;justify-content:space-between;align-items:center}
  .avatar{width:40px;height:40px;border-radius:10px;background:linear-gradient(135deg,#2563eb,#16a34a);display:flex;align-items:center;justify-content:center;font-size:17px;font-weight:900;color:#fff;flex-shrink:0}
  .camp-name{font-size:15px;font-weight:800;color:#0f172a}
  .camp-domain{font-size:11px;color:#94a3b8;font-family:monospace;margin-top:2px}

  .fraud-bar-row{padding:10px 22px;background:#fafafa;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;gap:12px}
  .fraud-bar-label{font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.8px;white-space:nowrap}
  .fraud-bar-track{flex:1;height:7px;background:#e2e8f0;border-radius:99px;overflow:hidden}
  .fraud-bar-fill{height:100%;border-radius:99px}

  .metrics{display:grid;grid-template-columns:repeat(4,1fr);border-bottom:1px solid #e2e8f0}
  .metric{padding:13px 16px;border-right:1px solid #e2e8f0;border-bottom:1px solid #e2e8f0}
  .metric:nth-child(4n){border-right:none}
  .metric:nth-child(n+5){border-bottom:none}
  .metric-label{font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:5px}
  .metric-value{font-size:22px;font-weight:900;font-family:monospace;line-height:1}

  .insights{margin:16px 22px;padding:14px 16px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:10px;border-left:4px solid #2563eb}
  .insight-line{font-size:11px;color:#374151;line-height:1.7;margin-bottom:4px}
  .insight-line strong{color:#0f172a}

  .section-title{font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:10px;padding-bottom:6px;border-bottom:1px solid #f1f5f9}

  .daily-section{padding:16px 22px;border-bottom:1px solid #e2e8f0}
  .daily-chart{display:flex;align-items:flex-end;gap:2px;height:60px;overflow:hidden}
  .day-col{display:flex;flex-direction:column;align-items:center;flex:1;min-width:18px}
  .day-bar{display:flex;flex-direction:column;justify-content:flex-end;width:100%}
  .day-label{font-size:7px;color:#94a3b8;text-align:center;margin-top:3px;line-height:1.2}
  .legend{font-size:9px;color:#94a3b8;margin-top:6px}

  .breakdown{display:grid;grid-template-columns:1fr 1fr 1fr;border-top:1px solid #e2e8f0}
  .breakdown-col{padding:16px 18px;border-right:1px solid #e2e8f0}
  .breakdown-col:last-child{border-right:none}
  .breakdown-title{font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:12px;padding-bottom:6px;border-bottom:1px solid #f1f5f9}
  .bar-row{margin-bottom:10px}
  .bar-label{font-size:11px;color:#475569;font-weight:500}
  .bar-track{height:4px;background:#e2e8f0;border-radius:99px;overflow:hidden;margin:3px 0}
  .bar-fill{height:100%;border-radius:99px}
  .no-data{font-size:11px;color:#94a3b8;font-style:italic}

  .pdf-footer{margin-top:36px;padding-top:18px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8}

  @media print{body,​.body{background:#fff}.campaign{box-shadow:none;page-break-inside:avoid}}
</style>
</head>
<body>
<div class="page">
  <div class="cover">
    <div>
      <div class="logo-mark">Click<span>Saver</span></div>
      <div class="logo-sub">Fraud Protection Platform</div>
    </div>
    <div style="text-align:right">
      <div class="report-title">${period.label} Report</div>
      <div class="report-sub">${periodLabel(period.days)}<br/>Generated ${now.toLocaleString()}<br/>${reportData.length} campaign${reportData.length !== 1 ? 's' : ''} analysed</div>
    </div>
  </div>

  <div class="summary">
    ${[
      { label: 'Total Ad Clicks', value: fmt(totalClicks), color: '#2563eb', bg: '#dbeafe' },
      { label: 'Fraud Detected', value: fmt(totalFraud), color: '#dc2626', bg: '#fee2e2' },
      { label: 'Detection Rate', value: `${overallRate}%`, color: '#d97706', bg: '#fef3c7' },
      { label: 'Est. Savings', value: `$${fmt(totalSavings, 2)}`, color: '#16a34a', bg: '#dcfce7' },
    ].map(s => `<div class="sum-item">
      <div class="sum-icon" style="background:${s.bg};color:${s.color}">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="${s.color}" stroke-width="2"/><path d="M8 12h8M12 8v8" stroke="${s.color}" stroke-width="1.5" stroke-linecap="round"/></svg>
      </div>
      <div><div class="sum-label" style="color:${s.color}">${s.label}</div><div class="sum-val" style="color:${s.color}">${s.value}</div></div>
    </div>`).join('')}
  </div>

  <div class="body">
    ${campBlocks}
    <div class="pdf-footer">
      <span>Generated by ClickSaver · clicksaver.app · Fraud Protection Report</span>
      <span>${now.toLocaleString()}</span>
    </div>
  </div>
</div>
<script>window.onload=function(){window.print()}</script>
</body>
</html>`;
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function Reports({ campaigns: propCampaigns }) {
  const [campaigns, setCampaigns] = useState(propCampaigns || []);
  const [selected, setSelected] = useState([]);
  const [period, setPeriod] = useState(PERIODS[1]);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [reportData, setReportData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [notifSettings, setNotifSettings] = useState({ weekly: true, monthly: true });

  useEffect(() => {
    const list = propCampaigns || [];
    setCampaigns(list);
    setSelected(list.map(c => c.id));
  }, [propCampaigns]);

  useEffect(() => {
    if (!propCampaigns) {
      campaignsAPI.list().then(d => {
        const list = Array.isArray(d) ? d : (d.campaigns || []);
        setCampaigns(list);
        setSelected(list.map(c => c.id));
      }).catch(() => {});
    }
    reportsAPI.getHistory().then(setHistory).catch(() => {}).finally(() => setHistoryLoading(false));
    notificationsAPI.get().then(d => setNotifSettings({ weekly: d.notify_weekly_report ?? true, monthly: d.notify_monthly_report ?? true })).catch(() => {});
  }, []);

  const toggleAll = () => setSelected(selected.length === campaigns.length ? [] : campaigns.map(c => c.id));
  const toggle = id => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleGenerate = async () => {
    if (!selected.length) { setError('Select at least one campaign.'); return; }
    if (period.key === 'custom') {
      if (!customStart || !customEnd) { setError('Please select both a start and end date.'); return; }
      if (customStart > customEnd) { setError('Start date must be before end date.'); return; }
    }
    setError(''); setGenerating(true); setReportData(null);
    let start, end, days;
    if (period.key === 'custom') {
      start = customStart; end = customEnd;
      days = Math.round((new Date(end) - new Date(start)) / 86400000) + 1;
    } else {
      ({ start, end } = isoRange(period.days));
      days = period.days;
    }
    try {
      const results = await Promise.all(
        campaigns.filter(c => selected.includes(c.id)).map(async campaign => {
          const data = await dashboardAPI.getCombined(campaign.id, days, start, end);
          return { campaign, stats: data.stats || {} };
        })
      );
      setReportData(results);
    } catch { setError('Failed to generate report. Please try again.'); }
    finally { setGenerating(false); }
  };

  const handleDownloadPDF = () => {
    if (!reportData) return;
    const w = window.open('', '_blank');
    w.document.write(buildPDF(reportData, period));
    w.document.close();
  };

  return (
    <div>
      <div className="page-header">
        <h1>Reports</h1>
        <p>Generate fraud protection reports for your campaigns and download as PDF</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Campaigns</div>
            <button onClick={toggleAll} className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 10px' }}>
              {selected.length === campaigns.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div style={{ padding: '10px 12px', maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {campaigns.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: 8 }}>No campaigns found.</div>}
            {campaigns.map(c => {
              const on = selected.includes(c.id);
              return (
                <button key={c.id} onClick={() => toggle(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, border: `1px solid ${on ? 'rgba(88,166,255,0.3)' : 'var(--border)'}`, background: on ? 'rgba(88,166,255,0.07)' : 'transparent', cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'all 0.15s' }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${on ? 'var(--accent-blue)' : 'var(--border)'}`, background: on ? 'var(--accent-blue)' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                    {on && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: on ? 'var(--text-primary)' : 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>{c.domain}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Report Period</div>
          </div>
          <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
            {PERIODS.map(p => {
              const on = period.key === p.key;
              return (
                <button key={p.key} onClick={() => setPeriod(p)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${on ? 'var(--accent-blue)' : 'var(--border)'}`, background: on ? 'rgba(88,166,255,0.07)' : 'transparent', cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'all 0.15s' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: on ? 'var(--accent-blue)' : 'var(--bg-secondary)', color: on ? '#fff' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{p.icon}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: on ? 'var(--accent-blue)' : 'var(--text-primary)' }}>{p.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{periodLabel(p.days)}</div>
                  </div>
                  {on && <svg style={{ marginLeft: 'auto' }} width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" fill="#58a6ff" opacity="0.2"/><path d="M5 8l2 2 4-4" stroke="#58a6ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </button>
              );
            })}

            {/* Custom date range */}
            {(() => {
              const on = period.key === 'custom';
              return (
                <div>
                  <button
                    onClick={() => setPeriod({ key: 'custom', label: 'Custom', icon: '—' })}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${on ? 'var(--accent-blue)' : 'var(--border)'}`, background: on ? 'rgba(88,166,255,0.07)' : 'transparent', cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'all 0.15s' }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: on ? 'var(--accent-blue)' : 'var(--bg-secondary)', color: on ? '#fff' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="2.5" width="13" height="12" rx="2" stroke="currentColor" strokeWidth="1.3"/><path d="M1.5 6h13M5 1v3M11 1v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: on ? 'var(--accent-blue)' : 'var(--text-primary)' }}>Custom Range</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                        {on && customStart && customEnd ? `${customStart} – ${customEnd}` : 'Pick any start & end date'}
                      </div>
                    </div>
                    {on && <svg style={{ marginLeft: 'auto' }} width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" fill="#58a6ff" opacity="0.2"/><path d="M5 8l2 2 4-4" stroke="#58a6ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </button>

                  {on && (
                    <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '0 2px' }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0.8, marginBottom: 4 }}>Start Date</div>
                        <input
                          type="date"
                          value={customStart}
                          max={customEnd || new Date().toISOString().slice(0, 10)}
                          onChange={e => setCustomStart(e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 8, border: '1.5px solid var(--accent-blue)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0.8, marginBottom: 4 }}>End Date</div>
                        <input
                          type="date"
                          value={customEnd}
                          min={customStart}
                          max={new Date().toISOString().slice(0, 10)}
                          onChange={e => setCustomEnd(e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 8, border: '1.5px solid var(--accent-blue)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>
                      {customStart && customEnd && (
                        <div style={{ gridColumn: '1/-1', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                          {Math.round((new Date(customEnd) - new Date(customStart)) / 86400000) + 1} days selected
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          {error && <div style={{ margin: '0 12px 12px', padding: '9px 12px', background: 'rgba(248,81,73,0.08)', border: '1px solid rgba(248,81,73,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--accent-red)' }}>{error}</div>}
          <div style={{ padding: '0 12px 12px' }}>
            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={generating || !selected.length || (period.key === 'custom' && (!customStart || !customEnd))}
              style={{ width: '100%', justifyContent: 'center', padding: '11px', fontSize: 13, fontWeight: 700 }}
            >
              {generating
                ? <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2, marginRight: 8 }} />Generating…</>
                : period.key === 'custom'
                  ? (customStart && customEnd ? `Generate Report (${Math.round((new Date(customEnd) - new Date(customStart)) / 86400000) + 1}d)` : 'Select dates above')
                  : `Generate ${period.label} Report`}
            </button>
          </div>
        </div>
      </div>

      {reportData && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{period.key === 'custom' ? 'Custom Range' : period.label} Report</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                {period.key === 'custom' ? `${customStart} – ${customEnd}` : periodLabel(period.days)} · {reportData.length} campaign{reportData.length !== 1 ? 's' : ''}
              </div>
            </div>
            <button className="btn btn-primary" onClick={handleDownloadPDF} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 20px', fontWeight: 700 }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2v9M5 8l3 3 3-3M3 13h10" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Download PDF
            </button>
          </div>
          {reportData.map(({ campaign, stats }) => (
            <CampaignReportCard key={campaign.id} campaign={campaign} stats={stats} period={period} />
          ))}
        </div>
      )}

      {/* ── Sent Reports History ── */}
      <div className="card" style={{ marginTop: 32, padding: 0, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Sent Reports</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Reports automatically sent to your email</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { label: 'Weekly', enabled: notifSettings.weekly, href: '/notifications' },
              { label: 'Monthly', enabled: notifSettings.monthly, href: '/notifications' },
            ].map(item => (
              <a key={item.label} href={item.href} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, border: `1px solid ${item.enabled ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`, background: item.enabled ? 'rgba(34,197,94,0.06)' : 'var(--bg-secondary)' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: item.enabled ? '#22c55e' : 'var(--text-muted)' }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: item.enabled ? '#22c55e' : 'var(--text-muted)' }}>{item.label}</span>
              </a>
            ))}
          </div>
        </div>

        {historyLoading ? (
          <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" style={{ width: 20, height: 20, borderWidth: 2, margin: '0 auto' }} /></div>
        ) : history.length === 0 ? (
          <div style={{ padding: '48px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📬</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>No reports sent yet</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 340, margin: '0 auto', lineHeight: 1.6 }}>
              Weekly reports are sent every Monday. Monthly reports on the 1st of each month.{' '}
              <a href="/notifications" style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}>Check your notification settings →</a>
            </div>
          </div>
        ) : (
          <div>
            {/* Table header */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 100px 120px 120px 80px', padding: '10px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
              {['Campaign', 'Sent To', 'Type', 'Date', 'Stats', 'Status'].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0.8 }}>{h}</div>
              ))}
            </div>
            {history.map((log, i) => {
              const d = new Date(log.sent_at);
              const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
              const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
              const isTest = log.is_test;
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 140px 100px 120px 120px 80px', padding: '12px 20px', borderBottom: i < history.length - 1 ? '1px solid var(--border)' : 'none', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.campaign_name}</div>
                    {isTest && <div style={{ fontSize: 10, color: 'var(--accent-blue)', marginTop: 1 }}>Test send</div>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.sent_to}</div>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: log.report_type === 'weekly' ? 'rgba(59,130,246,0.1)' : 'rgba(139,92,246,0.1)', color: log.report_type === 'weekly' ? '#3b82f6' : '#8b5cf6' }}>
                      {log.report_type === 'weekly' ? 'Weekly' : 'Monthly'}
                    </span>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{dateStr}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{timeStr}</div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>
                    {(log.total_clicks || 0).toLocaleString()} clicks<br />
                    <span style={{ color: '#ef4444' }}>{(log.fraud_clicks || 0).toLocaleString()} fraud</span>
                  </div>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: log.status === 'sent' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: log.status === 'sent' ? '#22c55e' : '#ef4444' }}>
                      {log.status === 'sent' ? '✓ Sent' : '✗ Failed'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
